import {Box} from '@dropbox/dig-foundations';
import {DSYS_NAVBAR_HEIGHT} from 'components/DSYS/layoutConstants';
import {ReactNode, useEffect, useRef, useState} from 'react';

import styles from './Dashboard.module.css';

const NAVBAR_HEIGHT_PX = parseInt(DSYS_NAVBAR_HEIGHT, 10) || 52;

/**
 * Wraps the dashboard search/omni bar so it sits in normal flow beneath the
 * greeting, then pins just below the global navbar once it scrolls up to it.
 * A sentinel above the bar drives a bottom fade that appears only while the bar
 * is stuck, so tiles read as scrolling underneath it.
 *
 * Pass `sticky={false}` (e.g. in customize/edit mode) to let the bar scroll away
 * normally, leaving the customize bar as the only pinned chrome.
 */
export const StickySearchBar = ({
  children,
  sticky = true,
}: {
  children: ReactNode;
  sticky?: boolean;
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (!sticky) {
      setStuck(false);
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), {
      rootMargin: `-${NAVBAR_HEIGHT_PX}px 0px 0px 0px`,
      threshold: 0,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sticky]);

  if (!sticky) {
    return <Box paddingY="12">{children}</Box>;
  }

  return (
    <>
      <Box ref={sentinelRef} aria-hidden height="0" />
      <Box className={styles.stickySearch} backgroundColor="Background Base" paddingY="12">
        {children}
        <div
          aria-hidden
          className={[styles.stickySearchShadow, stuck ? styles.stickySearchShadowVisible : '']
            .join(' ')
            .trim()}
        />
      </Box>
    </>
  );
};
